import { useMemo } from "react";
import type { EnduserObservation } from "@tellescope/types-models";
import type { LabResult, LabReading, StatusIndicator } from "@tellescope/ui";

const CLASSIFICATION_SEVERITY: Record<string, number> = {
  "critical high": 5,
  "very high": 4,
  high: 3,
  target: 1,
  normal: 1,
  low: 2,
  "very low": 4,
  "critical low": 5,
};

const CLASSIFICATION_TO_STATUS: Record<string, StatusIndicator> = {
  "critical high": "H",
  "very high": "H",
  high: "H",
  target: "N",
  normal: "N",
  low: "L",
  "very low": "L",
  "critical low": "L",
};

const STATUS_INDICATOR_MAP: Record<string, StatusIndicator> = { H: "H", L: "L", N: "N" };

const resolveStatusIndicator = (observation: EnduserObservation): StatusIndicator | undefined => {
  if (observation.statusIndicator) {
    const upper = observation.statusIndicator.toUpperCase();
    const mapped = STATUS_INDICATOR_MAP[upper];
    if (mapped) return mapped;
  }

  if (observation.classifications?.length) {
    let highestSeverity = 0;
    let resolvedStatus: StatusIndicator | undefined;

    for (const cls of observation.classifications) {
      const key = cls.classification.toLowerCase();
      const severity = CLASSIFICATION_SEVERITY[key] ?? 0;
      if (severity > highestSeverity) {
        highestSeverity = severity;
        resolvedStatus = CLASSIFICATION_TO_STATUS[key];
      }
    }

    return resolvedStatus;
  }

  return undefined;
};

const observationToReading = (observation: EnduserObservation): LabReading => ({
  id: observation.id,
  date: observation.timestamp instanceof Date ? observation.timestamp.toISOString() : String(observation.timestamp),
  value: observation.measurement.value,
  unit: observation.measurement.unit,
  refRange: observation.refRange,
  statusIndicator: resolveStatusIndicator(observation),
});

export const useScreeningDates = (observations: EnduserObservation[]): string[] => {
  return useMemo(() => {
    const dates = new Set<string>();
    for (const obs of observations) {
      const ts = obs.timestamp instanceof Date ? obs.timestamp : new Date(obs.timestamp);
      const dateStr = ts.toISOString().split("T")[0] ?? "";
      dates.add(dateStr);
    }
    return [...dates].sort((a, b) => b.localeCompare(a));
  }, [observations]);
};

export const useObservationLabResults = (observations: EnduserObservation[]): LabResult[] => {
  return useMemo(() => {
    const grouped = new Map<string, { displayName: string; observations: EnduserObservation[] }>();

    for (const obs of observations) {
      const unit = obs.measurement.unit;
      const key = obs.type ? `${unit}|${obs.type}` : unit;
      const displayName = obs.type ?? unit;
      const existing = grouped.get(key);
      if (existing) {
        existing.observations.push(obs);
      } else {
        grouped.set(key, { displayName, observations: [obs] });
      }
    }

    const results: LabResult[] = [];

    for (const [key, { displayName, observations: groupObs }] of grouped) {
      const readings = groupObs
        .map(observationToReading)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      results.push({
        id: key,
        name: displayName,
        category: groupObs[0]?.category ?? "vital-signs",
        readings,
      });
    }

    // Sort by most recent reading date
    results.sort((a, b) => {
      const aDate = a.readings[0] ? new Date(a.readings[0].date).getTime() : 0;
      const bDate = b.readings[0] ? new Date(b.readings[0].date).getTime() : 0;
      return bDate - aDate;
    });

    return results;
  }, [observations]);
};
